import { v4 } from 'uuid';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../context/complexDocumentContext';
import { DYNAMIC_TABLE_BLOCK, STATIC_TABLE_BLOCK, TEXT_BLOCK } from './complexDocumentConstants';
import { getNewVariableId, importVariables, modifyBlockContents, modifyDynamicTableBlockContents, modifyStaticTableBlockContents, modifyTextBlockContents, modifyVariableContract } from './importHelper';
import { deepCopy } from './javascript';
import { deleteBusinessLogic, deleteEmailBusinessLogic, updateBusinessLogic ,updateEmailBusinessLogic} from './services/businessLogicApi';
import { getMasterDocument } from './services/complexDocumentsAPI';
import { getMasterEmails } from './services/complexEmailAPI'
import { buildBusinessLogicMaps } from './helper';

export const addUuidToAllBlocksInDocument = (
  flows,
  includeHeaderFooter = true,
  includeFlows = true,
) => {
  return flows?.map(flow => {
    if (includeFlows && !flow.uuid) {
      flow.uuid = v4();
    }
    if (flow?.blocks?.length) {
      flow.blocks = flow.blocks.map(block => {
        if (!block.uuid) {
          block.uuid = v4();
        }
        return block;
      });
    }
    if (includeHeaderFooter) {
      if (flow?.header) {
        flow.header.uuid = v4();
      }
      if (flow?.footer) {
        flow.footer.uuid = v4();
      }
    }
    return flow;
  });
};

export const getAllBlocksInDocument = (flows, includeHeaderFooter = true) => {
  return flows?.flatMap(({ blocks, header, footer }) => {
    let totalblocks = [];
    if (blocks?.length) {
      totalblocks = [...blocks];
    }
    if (includeHeaderFooter) {
      if (header) {
        totalblocks = [...totalblocks, header];
      }
      if (footer) {
        totalblocks = [...totalblocks, footer];
      }
    }
    return totalblocks;
  });
};

export const importBusinessLogics = async (
  businessLogics,
  variableIdMap,
  oBlocks = [],
  syncData,
  store,
  isEmailTemplate,
  flowIdsMap,
  originalFlows
) => {
  return new Promise(async resolve => {
    const isMap = syncData instanceof Map;
    const newBlocks = getAllBlocksInDocument(syncData?.flows) || [];
    let i = 0;
    const currentStore = store?.get() || {};
    const newlyAddedBusinessLogics = [];
    for (; i < businessLogics.length; i++) {
      const businessLogic = businessLogics[i];
      businessLogic.documentId = syncData?.documentId || currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
      businessLogic.id = null;

      businessLogic.variables = businessLogic.variables.map(variable => {
        variable.variableId = getNewVariableId(variable.variableId, variableIdMap);
        return variable;
      });
      businessLogic.rules = businessLogic.rules.map(rule => {
        const usedVariableIds = [];
        const origUuid = oBlocks.find(oBlock => oBlock.blockId?.includes(rule.blockId))?.uuid;
        if (origUuid) {
          if (newBlocks?.length) {
            rule.blockId = newBlocks.find(nBlock => nBlock.uuid === origUuid)?.blockId;
          } else if (isMap) {
            rule.blockId =
              currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][syncData.get(origUuid)];
          }
        }
        if (rule.flowId && flowIdsMap && originalFlows) {
          const origFlowUuid = originalFlows?.find(oFlow => oFlow.originalFlowId?.includes(rule.flowId))
            ?.uuid;
          if (origFlowUuid) {
            rule.flowId =
              currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][
                flowIdsMap.get(origFlowUuid)
              ];
          }
        }
        if (rule.conditions?.length) {
          rule.conditions = rule.conditions.map(condition => {
            usedVariableIds.push(condition.variableId);
            condition.variableId = getNewVariableId(condition.variableId, variableIdMap);
            return condition;
          });
        }
        if (rule.expression && usedVariableIds?.length) {
          usedVariableIds.forEach(varId => {
            rule.expression = rule.expression?.replaceAll(
              varId,
              getNewVariableId(varId, variableIdMap),
            );
          });
        }
        rule.id = null;
        return rule;
      });
      try {
        const response = isEmailTemplate ? await updateEmailBusinessLogic(businessLogic) : await updateBusinessLogic(businessLogic);
        if (response?.data) {
          newlyAddedBusinessLogics.push(response.data);
        }
      } catch (e) {
        console.error(e);
      }
    }
    if (i === newlyAddedBusinessLogics.length) {
      resolve(newlyAddedBusinessLogics);
    }
  });
};

export const updateMasterTemplateToDocument = async (store, templateId, singleFlow,templateType) => {
  const currentStore = store.get();
  let payload;
  if (templateType === 'emailTemplate') {
    payload = { draftEmailId: templateId };
  } else {
    payload = { draftDocumentId: templateId };
  }
  let flows, variableIdMap, blBlocks, businessLogics;
  try {
    const masterTemplateResponse = templateType === 'emailTemplate' ? await getMasterEmails(payload) : await getMasterDocument(payload);
    if (masterTemplateResponse?.data?.length) {
      const masterResponse = masterTemplateResponse.data[0];
      const globalVariables = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
      let variables = masterResponse.variables;
      const masterTemplate = masterResponse.documentDTO;
      businessLogics = masterTemplate.businessLogics;
      if (singleFlow) {
        flows = [singleFlow];
        const flatContact = masterTemplate?.variableContract?.flatMap(contract => {
            if (contract?.content) {
              return contract.content.flatMap(item => {
                if (item.content) {
                  return item.content;
                }
                return item;
              });
            }
            return contract;
          }) || [];
        variables = variables.filter(variable => {
          return !!flatContact.find(mVar => mVar.variableId === variable.id);
        });
      } else {
        flows = masterTemplateResponse.data[0].documentDTO.flows;
      }
      variableIdMap = await importVariables(variables, globalVariables, store);
      const uploadedUrlsMap = new Map();
      blBlocks = [];
      await modifyMasterTemplateFlows(flows, blBlocks, variableIdMap, uploadedUrlsMap);
    }
  } catch (e) {
    console.log(e);
  }

  const handleBusinessLogic = async (syncData, setBusinessLogics, isEmailTemplate = false, flowIdsMap, originalFlows) => {
    if (!singleFlow && businessLogics && variableIdMap && blBlocks && syncData) {
      const bls = await importBusinessLogics(businessLogics, variableIdMap, blBlocks, syncData, store, isEmailTemplate, flowIdsMap, originalFlows);
      if (bls?.length) {
        const currentStore = store.get();
        setBusinessLogics(bls);
        const {flowBusinessLogicMap, elementBusinessLogicMap} = buildBusinessLogicMaps(bls, currentStore);
        store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC]: flowBusinessLogicMap, [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: elementBusinessLogicMap });
      }
    }
  };

  return { flows, handleBusinessLogic };
};

export const removeCurrentBusinessLogics = async (businessLogics, setBusinessLogics, isEmailTemplate = false) => {
  if (businessLogics?.length) {
    for (let i = 0; i < businessLogics.length; i++) {
      const bl = businessLogics[i];
      if (isEmailTemplate) {
        await deleteEmailBusinessLogic({ id: bl.id });
      } else {
        await deleteBusinessLogic({ id: bl.id });
      }
    }
    setBusinessLogics([]);
  }
}

export const modifyMasterTemplateFlows = async (flows, blBlocks, variableIdMap, uploadedUrlsMap, uploadImages = false) => {
  for (let flowIndex = 0; flowIndex < flows.length; flowIndex++) {
    const flow = flows[flowIndex];
    flow.originalFlowId = flow.flowId;
    flow.flowId = undefined;
    flow.isStandalone = false;
    if (!flow.uuid) {
      flow.uuid = v4();
    }
    const blocks = flow.blocks;
    for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
      const block = blocks[blockIndex];
      block.uuid = v4();
      blBlocks.push(deepCopy(block));
      block.blockId = undefined;
      if (block.blockType === DYNAMIC_TABLE_BLOCK) {
        for (let dIndex = 0; dIndex < block.blockDTOList?.length; dIndex++) {
          const subBlock = block.blockDTOList[dIndex];
          subBlock.contents = await modifyBlockContents(
            subBlock.contents,
            variableIdMap,
            uploadedUrlsMap,
            uploadImages,
          );
          subBlock.variableContract = modifyVariableContract(
            subBlock.variableContract,
            variableIdMap,
          );
        }
        block.contents = modifyDynamicTableBlockContents(block.contents, variableIdMap);
      } else if (block.blockType === TEXT_BLOCK) {
        block.contents = await modifyTextBlockContents(block.contents, variableIdMap);
      } else if (block.blockType === STATIC_TABLE_BLOCK) {
        block.contents = await modifyStaticTableBlockContents(block.contents, variableIdMap, uploadedUrlsMap, uploadImages);
      } else {
        block.contents = await modifyBlockContents(
          block.contents,
          variableIdMap,
          uploadedUrlsMap,
          uploadImages,
        );
      }
      block.variableContract = modifyVariableContract(block.variableContract, variableIdMap);
    }
    if (flow.header) {
      const header = flow.header;
      header.uuid = v4();
      blBlocks.push(deepCopy(header));
      header.blockId = undefined;
      header.contents = await modifyBlockContents(
        header.contents,
        variableIdMap,
        uploadedUrlsMap,
        uploadImages,
      );
      header.variableContract = modifyVariableContract(header.variableContract, variableIdMap);
    }
    if (flow.footer) {
      const footer = flow.footer;
      footer.uuid = v4();
      blBlocks.push(deepCopy(footer));
      footer.blockId = undefined;
      footer.contents = await modifyBlockContents(
        footer.contents,
        variableIdMap,
        uploadedUrlsMap,
        uploadImages,
      );
      footer.variableContract = modifyVariableContract(footer.variableContract, variableIdMap);
    }
  }
};

const handleBusinessLogic = async (
  blockIdsMap,
  selectedTemplate,
  store,
  setBusinessLogics,
) => {
  const currentStore = store.get();
  const blBlocks = getAllBlocksInDocument(selectedTemplate.flows);
  const newlyAddedBusinessLogics = [];
  if (selectedTemplate?.businessLogics?.length) {
    for (let i = 0; i < selectedTemplate.businessLogics.length; i++) {
      const businessLogic = selectedTemplate.businessLogics[i];
      businessLogic.documentId = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
      businessLogic.id = null;
      businessLogic.rules = businessLogic.rules.map(rule => {
        rule.id = null;
        const origUuid = blBlocks.find(oBlock => oBlock.blockId?.includes(rule.blockId))?.uuid;
        if (origUuid) {
          rule.blockId =
            currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockIdsMap.get(origUuid)];
        }
        return rule;
      });
      try {
        const response = await updateEmailBusinessLogic(businessLogic);
        if (response?.data) {
          newlyAddedBusinessLogics.push(response.data);
        }
      } catch (e) {
        console.error(e);
      }
    }
    if (newlyAddedBusinessLogics.length) {
      setBusinessLogics(newlyAddedBusinessLogics);
    }
  }
};

export const replaceEmailTemplate = async (item, methods, businessLogics, setBusinessLogics) => {
  const currentStore = methods.get();
  const flowUUID = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0];
  methods.set({
    [COMPLEX_DOCUMENT_CONTEXT_KEYS.NEW_LANGUAGE_CODE]: item?.element?.language,
    [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_DRAG_ACTION]: true
  });
  const blocksIdsMap = new Map();
  if (item.element?.isMasterTemplate) {
    updateMasterTemplateToDocument(methods, item.element.id, '', 'emailTemplate').then(
      async ({ flows, handleBusinessLogic }) => {
        methods.setPage('replaceBlocks', {
          uuid: flowUUID,
          blocks: flows[0].blocks,
          style: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES],
          flowStyle: flows[0].style,
          blocksIdsMap: blocksIdsMap,
        });
        // delete all business logics and replace it with business logics from master template
        removeCurrentBusinessLogics(businessLogics, setBusinessLogics, true);
        setTimeout(() => {
          handleBusinessLogic && handleBusinessLogic(blocksIdsMap, setBusinessLogics, true);
        }, 1500);
      },
    );
  } else {
    let elementItem = deepCopy(item.element);

    if (item.element?.templateType === "block") {
      methods.setPage('replaceBlocks', {
        uuid: flowUUID,
        blocks: deepCopy(elementItem.blocks),
        style: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES],
        flowStyle: elementItem.style,
        blocksIdsMap: blocksIdsMap,
      });

    } else {
      const flows = addUuidToAllBlocksInDocument(elementItem.flows, false, false);
      methods.setPage('replaceBlocks', {
        uuid: flowUUID,
        blocks: deepCopy(flows[0].blocks),
        style: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES],
        flowStyle: flows[0].style,
        blocksIdsMap: blocksIdsMap,
      });
      // delete all business logics and replace it with business logics from my design template
      removeCurrentBusinessLogics(businessLogics, setBusinessLogics, true);
      setTimeout(() => {
        handleBusinessLogic(blocksIdsMap, elementItem, methods, setBusinessLogics);
      }, 1500);
    }
  }
};
